import { MapPin, Mail, Phone, MessageCircle, Clock, Instagram, Facebook } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE } from "../../data/site";
import { useBooking } from "./BookingModal";
import { useLang } from "../../i18n/LanguageContext";

export const Contact = () => {
  const { openBooking } = useBooking();
  const { t } = useLang();
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36 bg-background"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>{t.contact.eyebrow}</Eyebrow>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl mt-5 leading-tight">
              {t.contact.title}
            </h2>
            <p className="mt-5 text-muted-foreground font-light leading-relaxed max-w-md">
              {t.contact.p}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 space-y-5">
            <a
              data-testid="contact-whatsapp"
              href={`https://wa.me/${SITE.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 text-foreground hover:text-accent transition-colors font-medium"
            >
              <MessageCircle size={18} className="text-accent" /> WhatsApp · {SITE.whatsappDisplay}
            </a>
            <a
              data-testid="contact-call"
              href={`tel:+${SITE.phone}`}
              className="flex items-center gap-4 text-foreground/80 hover:text-accent transition-colors"
            >
              <Phone size={18} className="text-accent" /> {SITE.phoneDisplay}
            </a>
            <a
              data-testid="contact-email"
              href={`mailto:${SITE.email}`}
              className="flex items-center gap-4 text-foreground/80 hover:text-accent transition-colors"
            >
              <Mail size={18} className="text-accent" /> {SITE.email}
            </a>
            <a
              data-testid="contact-location"
              href={SITE.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 text-foreground/80 hover:text-accent transition-colors"
            >
              <MapPin size={18} className="text-accent" /> {SITE.address}
            </a>
            <div
              data-testid="contact-hours"
              className="flex items-center gap-4 text-foreground/80"
            >
              <Clock size={18} className="text-accent" /> {t.contact.hours}
            </div>
          </Reveal>
          <Reveal delay={0.25} className="mt-8 flex gap-4">
            <a
              data-testid="contact-instagram"
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 flex items-center justify-center border border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
            >
              <Instagram size={18} />
            </a>
            <a
              data-testid="contact-facebook"
              href={SITE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 flex items-center justify-center border border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
            >
              <Facebook size={18} />
            </a>
            <a
              data-testid="contact-google"
              href={SITE.googleProfile}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Find us on Google"
              className="w-11 h-11 flex items-center justify-center border border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
            >
              <MapPin size={18} />
            </a>
          </Reveal>
          <Reveal delay={0.35}>
            <button
              data-testid="contact-book-now"
              onClick={openBooking}
              className="mt-10 inline-flex items-center bg-accent text-accent-foreground text-xs uppercase tracking-luxe px-8 py-4 hover:bg-foreground hover:text-background transition-colors duration-300"
            >
              {t.common.book}
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal y={40} className="overflow-hidden border border-border">
            <iframe
              data-testid="contact-map"
              title="Tribe Beach, Tigaki, Kos"
              src={SITE.mapEmbed}
              className="w-full h-[360px] md:h-[520px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
